import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardMetricCards from "../components/Dashboard/DashboardMetricCards";
import QuickStatsBar from "../components/Dashboard/QuickStatsBar";
import LineChart from "../components/Charts/LineChart";
import PieChart from "../components/Charts/PieChart";
import DataTable from "../components/Tables/DataTable";
import { SkeletonDashboard } from "../components/Common/Skeleton";
import { useDashboardMetrics, useRealtimeBookings, useRealtimeFleet } from "../hooks/useRealTimeData";
import { analyticsService } from "../services/analyticsService";
import { activityService, ActivityItem } from "../services/activityService";
import { vehiclePerformanceService, VehiclePerformanceData } from "../services/vehiclePerformanceService";
import { systemHealthService, SystemHealthData } from "../services/systemHealthService";
import { Booking, BookingStatus, PaymentStatus } from "../types/booking";

interface DashboardMetrics {
  totalUsers: number;
  activeBookings: number;
  fleetUtilization: number;
  revenue: number;
  revenueChange: number;
  bookingsChange: number;
  utilizationChange: number;
  usersChange: number;
}

// Activity Icon Components
const ActivityIcon: React.FC<{ type: string }> = ({ type }) => {
  const iconClass = "w-5 h-5";
  
  switch (type) {
    case 'booking':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'payment':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      );
    case 'maintenance':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'fleet':
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

// Real-time indicator component
const RealTimeIndicator: React.FC<{ lastUpdated: Date | null; isStale?: boolean }> = ({ lastUpdated, isStale }) => {
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    const updateTimeAgo = () => {
      if (!lastUpdated) {
        setTimeAgo('Never');
        return;
      }
      const seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
      if (seconds < 10) setTimeAgo('Just now');
      else if (seconds < 60) setTimeAgo(`${seconds}s ago`);
      else if (seconds < 3600) setTimeAgo(`${Math.floor(seconds / 60)}m ago`);
      else setTimeAgo(`${Math.floor(seconds / 3600)}h ago`);
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  return (
    <div className="flex items-center gap-2 text-xs">
      <div className={`w-2 h-2 rounded-full ${isStale ? 'bg-yellow-500' : 'bg-green-500'} animate-pulse`} />
      <span className={isStale ? 'text-yellow-600' : 'text-gray-500'}>
        {isStale ? 'Stale data • ' : 'Live • '}{timeAgo}
      </span>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // Real-time data hooks - auto-refresh every 5 minutes (300000ms)
  // Set to longer intervals to prevent constant refreshing
  const { 
    metrics: dashboardData, 
    loading: metricsLoading, 
    error: metricsError,
    lastUpdated: metricsLastUpdated,
    refresh: refreshMetrics 
  } = useDashboardMetrics(300000); // 5 minutes

  const {
    data: recentBookings,
    loading: bookingsLoading,
    lastUpdated: bookingsLastUpdated,
    refresh: refreshBookings
  } = useRealtimeBookings(10, 300000); // 5 minutes

  const {
    data: fleetSummary,
    loading: fleetLoading,
    lastUpdated: fleetLastUpdated,
    refresh: refreshFleet
  } = useRealtimeFleet(300000); // 5 minutes

  // Local state for other data
  const [salesData, setSalesData] = useState<any[]>([]);
  const [fleetData, setFleetData] = useState<any[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [vehiclePerformance, setVehiclePerformance] = useState<VehiclePerformanceData[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealthData | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Transform dashboard data to metrics format - ALL FROM REAL DATA
  const metrics: DashboardMetrics | null = dashboardData ? {
    totalUsers: dashboardData.uniqueCustomers || 0, // Real unique customers from bookings
    activeBookings: dashboardData.activeBookings || 0,
    fleetUtilization: dashboardData.fleetUtilization || 0,
    revenue: dashboardData.totalRevenue || 0,
    revenueChange: dashboardData.revenueChange || 0, // Real calculated change
    bookingsChange: dashboardData.bookingsChange || 0, // Real calculated change
    utilizationChange: dashboardData.utilizationChange || 0,
    usersChange: dashboardData.customersChange || 0, // Real calculated change
  } : null;

  // Update fleet pie chart data when fleet summary changes
  useEffect(() => {
    if (fleetSummary) {
      const summary = fleetSummary as any;
      setFleetData([
        { name: "Available", value: summary.availableVehicles || 0, color: "#10B981" },
        { name: "In Use", value: summary.rentedVehicles || 0, color: "#3B82F6" },
        { name: "Maintenance", value: summary.maintenanceVehicles || 0, color: "#F59E0B" },
        { name: "Out of Service", value: summary.outOfServiceVehicles || 0, color: "#EF4444" },
      ]);
    }
  }, [fleetSummary]);

  // Update sales chart data from real booking data
  useEffect(() => {
    if (dashboardData?.revenueTrend && dashboardData.revenueTrend.length > 0) {
      setSalesData(dashboardData.revenueTrend);
    }
  }, [dashboardData]);

  // Load sales chart data - fallback only if no real data
  const loadSalesData = useCallback(async () => {
    // Only load if we don't have real data from dashboardData
    if (dashboardData?.revenueTrend && dashboardData.revenueTrend.length > 0) {
      return; // Use real data from dashboardData
    }
    
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);

      const salesResponse = await analyticsService.getSalesMetrics({
        startDate,
        endDate,
        period: "daily",
      });

      if (salesResponse.success && salesResponse.data) {
        const chartData = salesResponse.data.map((metric: any, index: number) => ({
          date: new Date(Date.now() - (6 - index) * 24 * 60 * 60 * 1000).toLocaleDateString(),
          revenue: metric.totalRevenue || 0,
          bookings: metric.totalBookings || 0,
        }));
        setSalesData(chartData);
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
    }
  }, [dashboardData]);

  // Load activities, vehicle performance, and system health
  useEffect(() => {
    const loadActivities = async () => {
      const activityData = await activityService.getRecentActivities(10);
      setActivities(activityData);
    };

    const loadVehiclePerformance = async () => {
      const performanceData = await vehiclePerformanceService.getVehiclePerformance();
      setVehiclePerformance(performanceData.slice(0, 10));
    };

    const loadSystemHealth = async () => {
      const healthData = await systemHealthService.getSystemHealth();
      setSystemHealth(healthData);
    };

    loadSalesData();
    loadActivities();
    loadVehiclePerformance();
    loadSystemHealth();

    // Refresh activities and health every 5 minutes (not 30 seconds)
    const activityInterval = setInterval(loadActivities, 300000);
    const healthInterval = setInterval(loadSystemHealth, 300000);

    return () => {
      clearInterval(activityInterval);
      clearInterval(healthInterval);
    };
  }, []); // Remove loadSalesData dependency to prevent re-running

  // Manual refresh all data
  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshMetrics(),
        refreshBookings(),
        refreshFleet(),
        loadSalesData()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const bookingColumns = [
    {
      key: "bookingReference",
      title: "Booking ID",
      sortable: true,
      render: (value: string) => value || "N/A",
    },
    {
      key: "customer",
      title: "Customer",
      sortable: true,
      render: (value: any, row: Booking) =>
        row.customer
          ? `${row.customer.firstName} ${row.customer.lastName}`
          : "N/A",
    },
    {
      key: "car",
      title: "Vehicle",
      sortable: true,
      render: (value: any, row: Booking) => {
        if (!row.car) return "N/A";
        // Try different property names - database uses 'brand' not 'make'
        const make = row.car.brand || row.car.make || '';
        const model = row.car.model || '';
        const name = row.car.name || '';
        // Return the best available combination
        if (make && model) return `${make} ${model}`;
        if (name) return name;
        if (make) return make;
        return "N/A";
      },
    },
    {
      key: "status",
      title: "Status",
      sortable: true,
      render: (value: string) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            value === "confirmed"
              ? "bg-blue-100 text-blue-800"
              : value === "in-progress"
              ? "bg-yellow-100 text-yellow-800"
              : value === "completed"
              ? "bg-green-100 text-green-800"
              : value === "pending"
              ? "bg-orange-100 text-orange-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "totalAmount",
      title: "Amount",
      sortable: true,
      render: (value: number) => `$${value?.toFixed(2) || "0.00"}`,
    },
    {
      key: "pickUpDate",
      title: "Pickup Date",
      sortable: true,
      render: (value: Date) => new Date(value).toLocaleDateString(),
    },
  ];

  // Loading state
  const isInitialLoading = metricsLoading && !dashboardData;

  if (isInitialLoading) {
    return <SkeletonDashboard />;
  }

  // Error state
  if (metricsError && !dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Dashboard Error</h3>
              <p className="text-sm text-red-700 mt-1">{metricsError}</p>
            </div>
          </div>
          <button
            onClick={handleRefreshAll}
            className="mt-4 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Dashboard Overview</h1>
            <p className="text-indigo-100 mt-1">Welcome back! Here's what's happening with your rental business today.</p>
            <div className="mt-2">
              <RealTimeIndicator lastUpdated={metricsLastUpdated} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick Actions */}
            <button
              onClick={() => window.location.href = '/bookings'}
              className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-all font-medium shadow-sm flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Booking
            </button>
            
            <button
              onClick={() => window.location.href = '/fleet'}
              className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Vehicle
            </button>

            <button
              onClick={handleRefreshAll}
              className="p-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all"
              disabled={isRefreshing}
              title="Refresh Dashboard"
            >
              <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
        
        {metricsError && (
          <div className="mt-4 px-4 py-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-lg text-yellow-100 text-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Some real-time data may be unavailable. Showing cached data.
          </div>
        )}
      </div>

      {/* Quick Stats Bar */}
      <QuickStatsBar
        stats={{
          todayBookings: dashboardData?.todayBookings || 0,
          vehiclesOnRoad: dashboardData?.rentedVehicles || 0,
          pendingPayments: dashboardData?.pendingPayments || 0,
          maintenanceAlerts: dashboardData?.maintenanceVehicles || 0,
          newCustomers: dashboardData?.newCustomersThisWeek || 0
        }}
      />

      {/* Enhanced Metrics Cards */}
      <DashboardMetricCards metrics={metrics} loading={metricsLoading && !metrics} />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="relative">
          <LineChart
            title="Revenue & Bookings Trend"
            data={salesData}
            xAxisKey="date"
            lines={[
              { key: "revenue", color: "#3B82F6", name: "Revenue ($)" },
              { key: "bookings", color: "#10B981", name: "Bookings" },
            ]}
            height={280}
            showDots={true}
          />
        </div>

        <div className="relative">
          <div className="absolute top-4 right-4 z-10">
            <RealTimeIndicator lastUpdated={fleetLastUpdated} />
          </div>
          <PieChart 
            title="Fleet Status Distribution" 
            data={fleetData} 
            height={280}
            innerRadius={50}
            outerRadius={90}
          />
        </div>
      </div>

      {/* Recent Bookings Table */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
            <div className="flex items-center gap-4">
              <RealTimeIndicator lastUpdated={bookingsLastUpdated} />
              <button
                onClick={refreshBookings}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                disabled={bookingsLoading}
              >
                <svg className={`w-4 h-4 ${bookingsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          <DataTable
            data={recentBookings || dashboardData?.recentBookings || []}
            columns={bookingColumns}
            pageSize={5}
            onRowClick={(booking) => {
              // Navigate to bookings page with the booking ID to highlight
              navigate(`/bookings?highlight=${booking._id}`);
            }}
          />
        </div>
      </div>

      {/* Activity and Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto sidebar-scroll">
            {activities.length > 0 ? (
              activities.map((activity) => (
                <div key={activity.id} className="flex items-center text-sm gap-3">
                  <div className={`w-2 h-2 ${activity.color} rounded-full flex-shrink-0`}></div>
                  <div className={`${activity.color.replace('bg-', 'text-')} flex-shrink-0`}>
                    <ActivityIcon type={activity.iconType} />
                  </div>
                  <span className="text-gray-600 flex-1 min-w-0">{activity.message}</span>
                  <span className="text-gray-400 text-xs whitespace-nowrap flex-shrink-0">
                    {activityService.formatTimeAgo(activity.timestamp)}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No recent activities
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              System Status
            </h3>
            <a
              href="/system-health"
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View Details →
            </a>
          </div>
          <div className="space-y-3">
            {systemHealth ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Status</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${systemHealthService.getStatusColor(systemHealth.api.status)}`}>
                    {systemHealth.api.status} ({systemHealth.api.responseTime}ms)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${systemHealthService.getStatusColor(systemHealth.database.status)}`}>
                    {systemHealth.database.status} ({systemHealth.database.responseTime}ms)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Website</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${systemHealthService.getStatusColor(systemHealth.website.status)}`}>
                    {systemHealth.website.status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Real-time Updates</span>
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Active (30s interval)
                  </span>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500 py-4">
                Loading system status...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vehicle Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Vehicle Performance & Utilization
        </h3>
        <div className="space-y-4">
          {vehiclePerformance.length > 0 ? (
            vehiclePerformance.map((vehicle) => (
              <div key={vehicle.vehicleId} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-900">
                    {vehicle.make} {vehicle.model} ({vehicle.year})
                  </span>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-600">
                      {vehicle.totalBookings} bookings
                    </span>
                    <span className="text-gray-600">
                      ${vehicle.totalRevenue.toFixed(0)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      vehicle.utilizationRate >= 60 ? 'bg-green-100 text-green-800' :
                      vehicle.utilizationRate >= 30 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {vehicle.utilizationRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${vehiclePerformanceService.getUtilizationColor(vehicle.utilizationRate)}`}
                    style={{ width: `${Math.min(vehicle.utilizationRate, 100)}%` }}
                  >
                    <span className="text-xs text-white px-2 leading-3">
                      {vehiclePerformanceService.getUtilizationLabel(vehicle.utilizationRate)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              No vehicle performance data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
